import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { HostFloorPlanTable } from 'restaurantAdmin/floorPlans/apiHelpers';
import { RESERVATIONS_OCCUPANTS_PAGE_PATH } from 'restaurantAdmin/paths';
import {
  changeWalkInSeat,
  getWalkInAvailability,
  seatWalkIn,
  WalkInAvailability,
} from 'restaurantAdmin/reservations/apiHelpers';
import { errorToast } from '@components/toasts/Toasts';
import { ApiError } from '@shared/api/errors';
import { reportAppError } from '@shared/reportAppError';
import { useMutation, useQueries } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ServiceWalkIn } from '../apiHelpers';
import { WalkInFormData } from './SeatWalkInFormDrawer';

export const useWalkInAvailabilities = ({
  floorPlanTables,
  guestCount,
}: {
  floorPlanTables: HostFloorPlanTable[];
  guestCount?: number;
}) => {
  const { id: restaurantId } = useRestaurant();

  return useQueries({
    queries: floorPlanTables.map(({ id }) => ({
      queryKey: [restaurantId, 'walkInAvailability', id, guestCount],
      queryFn: async () =>
        getWalkInAvailability({
          floorPlanTableId: id,
          guestCount: guestCount!,
          restaurantId,
        }),
      enabled: !!guestCount && !!restaurantId,
    })),
    combine: (results) => {
      return {
        data: results
          .map((result) => result.data)
          .filter((d): d is WalkInAvailability => d !== undefined),
        error: results.find((result) => !!result.error),
      };
    },
  });
};

export const useSeatWalkIn = ({ onSuccess }: { onSuccess: () => void }) => {
  const navigate = useNavigate();
  const { id: restaurantId } = useRestaurant();

  return useMutation({
    mutationFn: (
      payload: WalkInFormData & { floorPlanTables: HostFloorPlanTable[] },
    ) =>
      seatWalkIn({
        restaurantId,
        floorPlanTableIds: payload.floorPlanTables.map((table) => table.id),
        guestCount: payload.guestCount,
        ...(payload.firstName && { firstName: payload.firstName }),
        ...(payload.lastName && { lastName: payload.lastName }),
        saveAsGuest: payload.saveAsGuest,
        guestId: payload.guestId,
        serverId: payload.serverId || null,
        ignoreAvailability: true,
        // `turnTime` and `hasAvailability` are deprecated but still required by
        // the API. We will remove them in an upcoming PR.
        turnTime: null,
        hasAvailability: true,
      }),
    onSuccess: () => {
      onSuccess();
      navigate(RESERVATIONS_OCCUPANTS_PAGE_PATH);
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        errorToast({ message: error.message });
      }
      reportAppError(error);
    },
  });
};

export const useChangeWalkInSeat = ({
  onSuccess,
}: {
  onSuccess: () => void;
}) => {
  const navigate = useNavigate();
  const { id: restaurantId } = useRestaurant();

  return useMutation({
    mutationFn: ({
      walkInId,
      floorPlanTables,
    }: {
      walkInId: ServiceWalkIn['id'];
      floorPlanTables: HostFloorPlanTable[];
    }) => {
      return changeWalkInSeat({
        walkInId,
        restaurantId,
        floorPlanTableIds: floorPlanTables.map((table) => table.id),
        ignoreAvailability: true,
        // `turnTime` and `hasAvailability` are deprecated but still required by
        // the API. We will remove them in an upcoming PR.
        turnTime: null,
        hasAvailability: true,
      });
    },
    onSuccess: () => {
      onSuccess();
      navigate(RESERVATIONS_OCCUPANTS_PAGE_PATH);
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        errorToast({ message: error.message });
      } else {
        errorToast({
          message: 'Failed to change walk-in seat. Please try again.',
        });
      }
      reportAppError(error);
    },
  });
};
